import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useProfile } from '../auth/ProfileContext';
import { bootstrapWorkspaces, renameWorkspace, Workspace } from './api';

interface WorkspaceContextValue {
  workspaces: Workspace[];
  loading: boolean;
  error: string;
  reload: () => Promise<void>;
  rename: (workspaceId: string, name: string) => Promise<void>;
}
const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authFetch, status, user } = useAuth();
  const { profile } = useProfile();
  const userId = status === 'signedIn' && profile?.userId === user?.userId ? profile?.userId : undefined;
  const identity = useRef(userId);
  identity.current = userId;
  const generation = useRef(0);
  const [state, setState] = useState<{ userId?: string; workspaces: Workspace[]; loading: boolean; error: string }>({
    workspaces: [], loading: false, error: '',
  });

  const reload = useCallback(async () => {
    const request = ++generation.current;
    if (!userId) { setState({ workspaces: [], loading: false, error: '' }); return; }
    setState({ userId, workspaces: [], loading: true, error: '' });
    try {
      const workspaces = await bootstrapWorkspaces(authFetch);
      if (identity.current === userId && generation.current === request) {
        setState({ userId, workspaces, loading: false, error: '' });
      }
    } catch (reason) {
      if (identity.current === userId && generation.current === request) {
        setState({ userId, workspaces: [], loading: false, error: reason instanceof Error ? reason.message : 'Could not load your workspaces.' });
      }
    }
  }, [authFetch, userId]);

  useEffect(() => {
    reload();
    return () => { generation.current += 1; };
    // Re-evaluate compatibility capabilities when an administrator changes access.
  }, [reload, profile?.role, profile?.accessStatus, profile?.isAdmin, profile?.suspended]);

  const rename = useCallback(async (workspaceId: string, name: string) => {
    if (!userId) throw new Error('Sign in to change workspace settings.');
    const request = generation.current;
    const updated = await renameWorkspace(authFetch, workspaceId, name);
    if (identity.current !== userId || generation.current !== request) throw new Error('Your account changed. Reopen the workspace to continue.');
    setState(current => ({ ...current, workspaces: current.workspaces.map(workspace => workspace.workspaceId === updated.workspaceId ? updated : workspace) }));
  }, [authFetch, userId]);

  const value = useMemo(() => ({
    workspaces: userId && state.userId === userId ? state.workspaces : [],
    loading: Boolean(userId) && (state.userId !== userId || state.loading),
    error: state.userId === userId ? state.error : '',
    reload, rename,
  }), [reload, rename, state, userId]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export function useWorkspaces(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('useWorkspaces must be used inside WorkspaceProvider');
  return value;
}
